import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { supabase } from "@/lib/supabase";
import { uploadImage } from "@/lib/storage";
import { formatBytes, formatDate, logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { MediaItem } from "@/lib/types";
import { PageHeader, Button, Card, Spinner, EmptyState, ConfirmDialog } from "@/pages/admin/components/ui";

export default function Media() {
  const { success, error: toastError } = useToast();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<MediaItem | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("media").select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as MediaItem[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await uploadImage(file, "media");
      await logActivity("subió una imagen a Media", file.name);
      success("Imagen subida correctamente.");
      load();
    } catch {
      toastError("No se pudo subir la imagen.");
    } finally {
      setUploading(false);
    }
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      success("URL copiada al portapapeles.");
    } catch {
      toastError("No se pudo copiar la URL.");
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    const { error } = await supabase.from("media").delete().eq("id", deleting.id);
    if (error) {
      toastError("No se pudo eliminar la imagen.");
      return;
    }
    await logActivity("eliminó una imagen de Media", deleting.nombre || undefined);
    success("Imagen eliminada.");
    setDeleting(null);
    load();
  }

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((m) => (m.nombre || "").toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div>
      <PageHeader
        title="Media"
        subtitle={`${items.length} archivo${items.length === 1 ? "" : "s"}`}
        actions={
          <Button onClick={() => fileInput.current?.click()} disabled={uploading}>
            {uploading ? (
              <><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Subiendo…</>
            ) : (
              <><i className="ri-upload-2-line" /> Subir imagen</>
            )}
          </Button>
        }
      />
      <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={onFile} className="hidden" />

      <div className="mb-6">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre…"
          className="w-full sm:max-w-xs px-3.5 py-2.5 rounded-md border border-background-300 bg-white text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {loading ? (
        <Spinner label="Cargando biblioteca…" />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon="ri-image-line"
            title={items.length === 0 ? "Aún no hay imágenes" : "Sin resultados"}
            description={items.length === 0 ? "Sube tu primera imagen para empezar a construir la biblioteca." : "Prueba con otra búsqueda."}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} className="group bg-white border border-background-200 rounded-lg overflow-hidden">
              <div className="relative aspect-[4/3] bg-background-100">
                <img src={m.url} alt={m.nombre || "imagen"} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/40 transition-colors" />
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => copyUrl(m.url)} className="w-8 h-8 flex items-center justify-center rounded-md bg-white/90 text-foreground-700 hover:bg-primary-500 hover:text-white" title="Copiar URL"><i className="ri-link" /></button>
                  <button onClick={() => setDeleting(m)} className="w-8 h-8 flex items-center justify-center rounded-md bg-white/90 text-foreground-700 hover:bg-red-600 hover:text-white" title="Eliminar"><i className="ri-delete-bin-line" /></button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs font-medium text-foreground-800 truncate">{m.nombre || "imagen"}</p>
                <p className="text-[11px] text-foreground-400 mt-0.5">{formatBytes(m.tamano)} · {formatDate(m.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={Boolean(deleting)} title="Eliminar imagen" message={`¿Seguro que quieres eliminar "${deleting?.nombre || "esta imagen"}"?`} onConfirm={confirmDelete} onCancel={() => setDeleting(null)} />
    </div>
  );
}